import PropTypes from "prop-types";
import React from "react";

import { fireTrack } from "@swa-ui/analytics";
import { useLocation } from "@swa-ui/browser";
import { Banner } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

import { PlacementFactory } from "../PlacementFactory";
import { usePlacementIds } from "../usePlacementIds";

const ANALYTICS_EXCEPTION_PATH = "/vacations";
const NUMERIC_REGEX = /\d+/;

/**
 * A component that renders a rotating banner with multiple placement items sorted ascending numerically by id (i.e. indexRotatingBannerPanel1 then indexRotatingBannerPanel2...e.t.c.).
 * The banner includes accessibility labels and analytics tracking functionality.
 *
 * @param {Object} props.placement - Object that displays placement content
 * @param {string} props.placement.id - Placement type identifier used to match placement IDs
 * @param {Object} props.placementData - Object containing template and analytics values
 * @param {string} props.placementData.analyticsPath - Dynamic path used for analytics event description
 * @param {string} props.placementData.analyticsValue - Dynamic value used for analytics event description
 */

export const RotatingBannerPanel = (props) => {
  const { placement = {}, placementData = {} } = props;
  const { analyticsValue = "" } = placementData;
  const { pathname } = useLocation();
  const sortedPlacementIds = usePlacementIds(new RegExp(`${placement?.id}.*`)).sort(
    comparePlacementIds
  );

  return sortedPlacementIds.length > 0 && <Banner {...getBannerProps()} />;

  function getBannerProps() {
    return {
      "aria-label": i18n("RotatingBannerPanel__ARIA_LABEL"),
      "aria-label-next": i18n("RotatingBannerPanel__ARIA_LABEL_NEXT"),
      "aria-label-previous": i18n("RotatingBannerPanel__ARIA_LABEL_PREVIOUS"),
      items: sortedPlacementIds.map((placementId) => (
        <PlacementFactory id={placementId} key={placementId} />
      )),
      onClick,
      width: "preset",
    };
  }

  function onClick() {
    if (analyticsValue && pathname !== ANALYTICS_EXCEPTION_PATH) {
      fireTrack("squid", { page_description: analyticsValue });
    }
  }

  function comparePlacementIds(placementId1, placementId2) {
    return (
      parseInt(placementId1.match(NUMERIC_REGEX)[0]) -
      parseInt(placementId2.match(NUMERIC_REGEX)[0])
    );
  }
};

RotatingBannerPanel.displayName = "RotatingBannerPanel";
RotatingBannerPanel.propTypes = {
  /** Object that displays placement content. */
  placement: PropTypes.shape({
    /** Placement type id */
    id: PropTypes.string,
  }),

  /** Object that contains string values corresponding to templateKeys and/or dynamic analytics values for the accordionBlock. */
  placementData: PropTypes.shape({
    /** The dynamic path that will be used as an event description for analytics. */
    analyticsPath: PropTypes.string,

    /** The dynamic value that will be used as an event description for analytics. */
    analyticsValue: PropTypes.string,
  }),
};
